import type { Ref } from 'vue';

import { useApolloClient, useMutation } from '@vue/apollo-composable';
import { computed, ref, toValue, watch } from 'vue';

import type { FiscalWorkstationStorage } from '@/entities/fiscalization';

import { useAuthStore } from '@/entities/auth';
import { useFiscalWorkstationStorage } from '@/entities/fiscalization';
import { apolloOperationGate, getKkmServerAdapter } from '@/shared/api';
import { requirePayload } from '@/shared/lib';
import {
  AnswerOrderFiscalReceiptPrintedDocument,
  CancelOrderFiscalPaymentDocument,
  CompleteOrderFiscalSettlementDocument,
  FailOrderFiscalSettlementDocument,
  GetOrderCheckoutContextDocument,
  GetOrderFiscalSettlementOperationDocument,
  type OrderFieldsFragment,
  type OrderSource,
  PrepareOrderFiscalSettlementDocument,
  type PrepareOrderFiscalSettlementInput,
  ResolveOrderFiscalReceiptDocument,
  RetryOrderFiscalReceiptDocument,
  StartOrderFiscalSettlementDocument,
} from '@shared/api/graphql/gql/graphql';

import type { OrderFiscalizationPhase, OrderFiscalizationState } from './order-fiscalization-state';

import { requestCashShiftUi } from './cash-shift-ui-events';
import { FISCAL_MUTATION_OPTIONS, createFiscalPaymentMutationOptions } from './fiscal-mutation-options';
import { OrderFiscalizationController } from './order-fiscalization-controller';
import { BUSY_PHASES, createInitialOrderFiscalizationState } from './order-fiscalization-state';

export interface UseOrderFiscalizationOptions {
  /** Привязанная ККТ локации (`orderFiscalOperationalStatus.expected_kkt_device_id`). */
  expectedKktDeviceId?: Ref<string | null | undefined>;
  locationId: Ref<string | null | undefined>;
  organizationId: Ref<string | null | undefined>;
  /** Источник заказов страницы: незавершённая оплата запоминается и продолжается с ним. */
  source?: OrderSource;
}

export function useOrderFiscalization(options: UseOrderFiscalizationOptions) {
  const { resolveClient } = useApolloClient();
  const adapter = getKkmServerAdapter();
  const authStore = useAuthStore();
  const state = ref<OrderFiscalizationState>(createInitialOrderFiscalizationState());
  const paymentMutationOptions = createFiscalPaymentMutationOptions();
  const { mutate: prepareMutation } = useMutation(PrepareOrderFiscalSettlementDocument, FISCAL_MUTATION_OPTIONS);
  const { mutate: startMutation } = useMutation(StartOrderFiscalSettlementDocument, FISCAL_MUTATION_OPTIONS);
  const { mutate: completeMutation } = useMutation(CompleteOrderFiscalSettlementDocument, paymentMutationOptions);
  const { mutate: failMutation } = useMutation(FailOrderFiscalSettlementDocument, paymentMutationOptions);
  const { mutate: retryReceiptMutation } = useMutation(RetryOrderFiscalReceiptDocument, FISCAL_MUTATION_OPTIONS);
  const { mutate: resolveReceiptMutation } = useMutation(ResolveOrderFiscalReceiptDocument, paymentMutationOptions);
  const { mutate: answerReceiptMutation } = useMutation(
    AnswerOrderFiscalReceiptPrintedDocument,
    paymentMutationOptions,
  );
  const { mutate: cancelPaymentMutation } = useMutation(CancelOrderFiscalPaymentDocument, paymentMutationOptions);
  let controller: OrderFiscalizationController | null = null;

  // Смена организации/локации сбрасывает контроллер синхронно: следующий вызов создаст новый.
  watch(
    () => `${toValue(options.organizationId)}:${toValue(options.locationId)}`,
    () => {
      state.value = createInitialOrderFiscalizationState();
      controller = null;
    },
    { flush: 'sync' },
  );

  const scopedController = (): OrderFiscalizationController => {
    const organizationId = toValue(options.organizationId);
    const locationId = toValue(options.locationId);
    if (!organizationId || !locationId) {
      throw new Error('Для фискализации выберите организацию и локацию.');
    }

    if (controller !== null) return controller;

    const workstation = useFiscalWorkstationStorage(organizationId, locationId);
    const storage: FiscalWorkstationStorage = {
      ...workstation,
      addPendingOperation: (operationId, orderId) =>
        workstation.addPendingOperation(operationId, orderId, options.source),
    };
    const nextController = new OrderFiscalizationController({
      adapter,
      timezone: () => (authStore.currentLocation?.id === locationId ? authStore.currentLocation.timezone : undefined),
      expectedKktDeviceId: () => toValue(options.expectedKktDeviceId),
      onState: (nextState) => {
        // Поздний callback контроллера, сброшенного сменой локации, состояние не трогает.
        if (controller !== nextController) return;
        state.value = { ...nextState };
      },
      services: {
        cancelPayment: async (input) =>
          requirePayload(
            (await cancelPaymentMutation({ input }))?.data?.cancelOrderFiscalPayment,
            'Mors не зафиксировал отмену платежа.',
          ),
        complete: async (input) =>
          requirePayload(
            (await completeMutation({ input }))?.data?.completeOrderFiscalSettlement,
            'Mors не подтвердил результат фискализации.',
          ),
        fail: async (input) =>
          requirePayload(
            (await failMutation({ input }))?.data?.failOrderFiscalSettlement,
            'Mors не зафиксировал отказ фискальной операции.',
          ),
        find: async (orderId) => {
          const response = await resolveClient().query({
            fetchPolicy: 'network-only',
            query: GetOrderFiscalSettlementOperationDocument,
            variables: { orderId },
          });
          return response.data.orderFiscalSettlementOperation ?? null;
        },
        refreshOrder: async (orderId) => {
          const response = await resolveClient().query({
            fetchPolicy: 'network-only',
            query: GetOrderCheckoutContextDocument,
            variables: { orderId },
          });
          return requirePayload(response.data.orderCheckoutContext?.order, 'Не удалось обновить результат оплаты.');
        },
        resolveReceipt: async (input) =>
          requirePayload(
            (await resolveReceiptMutation({ input }))?.data?.resolveOrderFiscalReceipt,
            'Mors не принял документы кассы.',
          ),
        answerReceiptPrinted: async (input) =>
          requirePayload(
            (await answerReceiptMutation({ input }))?.data?.answerOrderFiscalReceiptPrinted,
            'Mors не сохранил ответ о чеке.',
          ),
        retryReceipt: async (input) =>
          requirePayload(
            (await retryReceiptMutation({ input }))?.data?.retryOrderFiscalReceipt,
            'Mors не разрешил повтор печати чека.',
          ),
        prepare: async (input) =>
          requirePayload(
            (await prepareMutation({ input }))?.data?.prepareOrderFiscalSettlement,
            'Mors не подготовил фискальную операцию.',
          ),
        start: async (input) =>
          requirePayload(
            (await startMutation({ input }))?.data?.startOrderFiscalSettlement,
            'Mors не запустил фискальную операцию.',
          ),
      },
      storage,
    });
    controller = nextController;
    return controller;
  };

  // Пока оплата ждёт очереди шлюза (проверка кассы), форма открыта и кнопка активна. Второй
  // клик встал бы в очередь с тем же ключом попытки и после первой оплаты повторил бы prepare.
  let settling = false;
  const settle = async (
    input: Omit<PrepareOrderFiscalSettlementInput, 'browserWorkstationId' | 'kktDeviceId' | 'kkt' | 'terminal'>,
  ): Promise<OrderFieldsFragment | null> => {
    if (settling) return null;
    settling = true;
    try {
      return await apolloOperationGate.runExclusive('fiscalization', () => scopedController().settle(input));
    } finally {
      settling = false;
    }
  };
  const recover = async (orderId: string): Promise<OrderFieldsFragment | null> =>
    apolloOperationGate.runExclusive('fiscalization', () => scopedController().recover(orderId));
  /** Цикл смены без шлюза: действие смены уже держит `apolloOperationGate`. Возвращает итоговую фазу. */
  const recoverInShiftCycle = async (orderId: string): Promise<OrderFiscalizationPhase> => {
    const current = scopedController();
    await current.recover(orderId, { startPayment: false });

    return current.state.phase;
  };
  const retryFinalize = async (): Promise<OrderFieldsFragment | null> =>
    apolloOperationGate.runExclusive('fiscalization', () => scopedController().retryFinalize());
  const retryReceipt = async (): Promise<OrderFieldsFragment | null> =>
    apolloOperationGate.runExclusive('fiscalization', () => scopedController().retryReceipt());
  const resolveReceipt = async (): Promise<OrderFieldsFragment | null> =>
    apolloOperationGate.runExclusive('fiscalization', () => scopedController().resolveReceipt());
  const answerReceiptPrinted = async (printed: boolean): Promise<OrderFieldsFragment | null> =>
    apolloOperationGate.runExclusive('fiscalization', () => scopedController().answerReceiptPrinted(printed));
  /** Заказы с незавершённой оплатой этого рабочего места и источника — для продолжения после перезагрузки. */
  const pendingSaleIds = (): readonly string[] => {
    const organizationId = toValue(options.organizationId);
    const locationId = toValue(options.locationId);

    return organizationId && locationId
      ? useFiscalWorkstationStorage(organizationId, locationId).pendingSaleIds(options.source)
      : [];
  };
  const cancelUnconfirmedPayment = async (): Promise<OrderFieldsFragment | null> =>
    apolloOperationGate.runExclusive('fiscalization', () => scopedController().cancelUnconfirmedPayment());
  const cancelWithoutCheck = async (): Promise<OrderFieldsFragment | null> =>
    apolloOperationGate.runExclusive('fiscalization', () => scopedController().cancelWithoutCheck());
  const reset = (): boolean => scopedController().reset();
  const cancelPayment = async (): Promise<boolean> => scopedController().cancelPayment();
  const fixShift = (): Promise<boolean> =>
    Promise.resolve(state.value.shiftAction === null ? false : requestCashShiftUi(state.value.shiftAction));
  // `printed_pending_sync` держит полный fiscal payload только в памяти контроллера:
  // уход со страницы уничтожает его вместе с pendingFinalize, а операция остаётся
  // на backend в STARTED/PAYMENT_CAPTURED.
  const isBlocking = computed(() =>
    [
      'waiting_payment',
      'checking_payment_result',
      'printing',
      'finalizing',
      'checking_receipt',
      'payment_unconfirmed',
      'cancelling_payment',
      'print_result_unknown',
      'payment_without_receipt',
      'printed_pending_sync',
    ].includes(state.value.phase),
  );
  const isBusy = computed(() => BUSY_PHASES.has(state.value.phase));

  return {
    answerReceiptPrinted,
    cancelPayment,
    cancelUnconfirmedPayment,
    cancelWithoutCheck,
    fixShift,
    isBlocking,
    isBusy,
    pendingSaleIds,
    recover,
    recoverInShiftCycle,
    reset,
    resolveReceipt,
    retryFinalize,
    retryReceipt,
    settle,
    state,
  };
}
